import { AfterViewInit, Component, inject, OnDestroy, OnInit } from '@angular/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconRegistry } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { DomSanitizer } from '@angular/platform-browser';
import { AuthService, UserInfo } from '@michelin/auth';
import {
  SidebarModule,
  LogoModule,
  NavbarModule,
  MainContentModule,
  AlertModule,
  IconModule,
  HeaderModule,
  AlertService,
  FooterModule
} from '@michelin/theme';
import { TranslatePipe } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { fromEvent, debounceTime, takeUntil, Subject } from 'rxjs';
import { TranslationService } from 'src/app/core/services/translation.service';
import { AnalyseState } from 'src/app/features/analyse/state/analyse.state';
import { AnalyseBreadcrumbAction } from '../../../features/analyse/state/analyse.actions';
import { ImportBreadcrumbAction } from '../../../features/imports/state/import.actions';
import { UserRolesService } from '../../../core/user-roles/user-roles-service';

@Component({
  templateUrl: './layout.component.html',
  styleUrls: ['./layout.component.scss'],
  standalone: true,
  imports: [
    NavbarModule,
    HeaderModule,
    LogoModule,
    SidebarModule,
    MainContentModule,
    AlertModule,
    MatFormFieldModule,
    MatSelectModule,
    TranslatePipe,
    IconModule,
    FooterModule
  ],
  providers: [AlertService]
})
export class LayoutComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly store = inject(Store);
  private readonly translationService = inject(TranslationService);
  private readonly matIconRegistry = inject(MatIconRegistry);
  private readonly domSanitizer = inject(DomSanitizer);
  private readonly authService = inject(AuthService);

  defaultLang = this.translationService.getCurrentLang();
  supportedLangs = this.translationService.getSupportedLangs();
  claims: Partial<UserInfo>;
  year = new Date().getFullYear();

  showAnalyse = this.store.selectSignal(AnalyseState.showAnalyse);

  private readonly destroy$ = new Subject<void>();
  private readonly DEBOUNCE_TIME = 300; // ms
  private readonly DEFAULT_FOOTER_TOP_POSITION = 10;

  constructor(private userRolesService: UserRolesService) {
    this.matIconRegistry.addSvgIcon('database', this.domSanitizer.bypassSecurityTrustResourceUrl('assets/icons/database.svg'));
    this.matIconRegistry.addSvgIcon('finance', this.domSanitizer.bypassSecurityTrustResourceUrl('assets/icons/finance.svg'));
    this.matIconRegistry.addSvgIcon('event_list', this.domSanitizer.bypassSecurityTrustResourceUrl('assets/icons/event_list.svg'));
    this.matIconRegistry.addSvgIcon('deployed', this.domSanitizer.bypassSecurityTrustResourceUrl('assets/icons/deployed.svg'));
  }

  ngOnInit(): void {
    this.claims = this.authService.getIdentityClaims();
  }

  setLanguage = (lang: string) => this.translationService.setLanguage(lang);
  logout = () => this.authService.logOut();

  ngAfterViewInit(): void {
    const micScrollBar = document.querySelector<HTMLElement>('[data-overlayscrollbars-viewport~=overflowYScroll]');
    const footer = document.querySelector<HTMLElement>('footer');
    const fab = document.querySelector<HTMLElement>('.floating-button');

    if (!footer || !fab) {
      return;
    }

    // Init first position
    this.updateFabPosition(fab, footer);

    if (!micScrollBar) {
      return;
    }

    fromEvent(micScrollBar, 'scroll')
      .pipe(debounceTime(this.DEBOUNCE_TIME), takeUntil(this.destroy$))
      .subscribe(() => {
        this.updateFabPosition(fab, footer);
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private readonly updateFabPosition = async (fab: HTMLElement, footer: HTMLElement): Promise<void> => {
    const footerRect = footer.getBoundingClientRect();
    const fabRect = fab.getBoundingClientRect();
    const visibleFooterHeight = this.footerVisibleHeight();

    if (fabRect.bottom > footerRect.top) {
      const newPosition = visibleFooterHeight + this.DEFAULT_FOOTER_TOP_POSITION;
      fab.style.bottom = `${newPosition}px`;
    } else {
      fab.style.bottom = `${this.DEFAULT_FOOTER_TOP_POSITION}px`;
    }
  };

  private readonly footerVisibleHeight = (): number => {
    const footer = document.querySelector('footer');
    if (!footer) return 0;

    const footerRect = footer.getBoundingClientRect();
    const viewportHeight = window.outerHeight;

    // Si le footer est complètement hors de la vue
    if (footerRect.top >= viewportHeight) {
      return 0;
    }

    // Si le footer est complètement visible
    if (footerRect.bottom <= viewportHeight) {
      return footerRect.height;
    }

    // Si le footer est partiellement visible
    return viewportHeight - footerRect.top;
  };

  resetAllBreadCrumb() {
    this.store.dispatch(new AnalyseBreadcrumbAction(1));
    this.store.dispatch(new ImportBreadcrumbAction(1));
  }

  onSidebarItemKeyDown(event: KeyboardEvent): void {
    if (event.code === 'Space') {
      event.preventDefault();
    }
  }

  get isCurrentUserAdmin(): boolean {
    const roles = this.userRolesService.getUserRoles();
    return roles.includes('Admin');
  }
}
